/** Public research evidence contracts. Captures, reviewed interpretations and model uses remain distinct. */
import * as z from 'zod';
const id = z.string().regex(/^[a-zA-Z0-9_-]{1,128}$/);
const hash = z.string().regex(/^[a-f0-9]{64}$/);
const text = z.string().trim().min(1);
const time = z.iso.datetime({ offset: true });
const https = z.url().refine((value) => new URL(value).protocol === 'https:', 'HTTPS required');
export const researchArchiveLimits = { sourceBytes: 10 * 1024 * 1024, textBytes: 2 * 1024 * 1024 };
const boundedText = (maxBytes) => z
    .string()
    .min(1)
    .max(maxBytes)
    .refine((value) => new TextEncoder().encode(value).byteLength <= maxBytes, 'UTF-8 byte limit exceeded');
const ids = z
    .array(id)
    .max(50)
    .refine((values) => new Set(values).size === values.length, 'Duplicate IDs');
export const researchSourceUseDecisionSchema = z.strictObject({
    sourceId: id,
    revision: z.number().int().positive(),
    reviewer: text.max(200),
    reason: text.max(4000),
    decidedAt: time,
    retentionUntil: time.nullable(),
    archive: z.boolean(),
    index: z.boolean(),
    modelContext: z.boolean(),
    merchantExcerpt: z.boolean(),
});
/** Confirming import/capture admits normal library use; this records an operator decision, not a licence. */
export function researchSourceAdmission(sourceId, reviewer, decidedAt) {
    return researchSourceUseDecisionSchema.parse({
        sourceId,
        revision: 1,
        reviewer,
        reason: 'Operator admission for research library use; not a publisher licence declaration',
        decidedAt,
        retentionUntil: null,
        archive: true,
        index: true,
        modelContext: true,
        merchantExcerpt: true,
    });
}
export const researchSourceVersionSchema = z.strictObject({
    sourceId: id,
    versionId: hash,
    url: https,
    finalUrl: https,
    publisher: text.max(500),
    title: text.max(1000),
    publishedAt: z.string().nullable(),
    capturedAt: time,
    checkedAt: time,
    transport: z.enum(['cached', 'firecrawl']),
    mime: text.max(200),
    extractionVersion: text.max(100),
    completeness: text.max(2000),
    contentHash: hash,
    normalizedTextHash: hash,
    sourceUse: researchSourceUseDecisionSchema,
});
export const researchPassageSchema = z
    .strictObject({
    passageId: hash,
    sourceVersionId: hash,
    normalizedTextHash: hash,
    startByte: z.number().int().nonnegative(),
    endByte: z.number().int().positive(),
    quote: z.string().min(1).max(16000),
    locator: z.string().max(500).nullable(),
})
    .refine((p) => p.endByte > p.startByte, 'Invalid byte range');
export const researchMappingsSchema = z.strictObject({
    conceptIds: ids,
    sourceIndustryIds: ids,
    topics: ids,
    origin: z.enum(['operator', 'agent-draft']),
    reviewer: text.max(200),
    vocabularyRevision: text.max(100),
});
export const researchCaseRevisionSchema = z.strictObject({
    caseId: id,
    revisionId: hash,
    title: text.max(500),
    classification: text.max(100),
    population: text.max(2000),
    intervention: text.max(4000),
    comparator: text.max(2000),
    outcome: text.max(4000),
    interpretation: text.max(16000),
    limitations: text.max(8000),
    applicability: text.max(4000),
    passageIds: z.array(hash).min(1).max(30),
    reviewedBy: text.max(200),
    reviewedAt: time,
});
export const researchImportSchema = z.strictObject({
    schemaVersion: z.literal(1),
    sources: z
        .array(z.strictObject({
        version: researchSourceVersionSchema,
        originalText: boundedText(researchArchiveLimits.sourceBytes),
        normalizedText: boundedText(researchArchiveLimits.textBytes),
    }))
        .min(1)
        .max(100),
    passages: z.array(researchPassageSchema).min(1).max(200),
    cases: z
        .array(z.strictObject({ revision: researchCaseRevisionSchema, mappings: researchMappingsSchema }))
        .min(1)
        .max(50),
});
export const researchCatalogueFilterSchema = z.strictObject({
    concepts: z.strictObject({ values: ids, match: z.enum(['any', 'all']) }).optional(),
    industries: z.strictObject({ values: ids, match: z.enum(['any', 'all']) }).optional(),
    topics: z.strictObject({ values: ids, match: z.enum(['any', 'all']) }).optional(),
    state: z.enum(['reviewed', 'withdrawn']).optional(),
    after: id.optional(),
    limit: z.number().int().min(1).max(100).default(50),
});
export const researchEvidenceRequestSchema = z.strictObject({
    schemaVersion: z.literal(1),
    purpose: z.enum(['opportunity_preparation', 'operator_research', 'research_cards']),
    question: text.max(4000),
    projectionVersion: z.number().int().positive(),
    relevanceContext: z.strictObject({ conceptIds: ids, sourceIndustryIds: ids, topics: ids }),
    budget: z.strictObject({
        maxCases: z.number().int().min(1).max(10),
        maxContextBytes: z.number().int().min(1).max(65536),
    }),
});
export const researchRetrievalSettingsSchema = z.strictObject({
    version: z.literal(1),
    mode: z.enum(['keyword', 'vector', 'hybrid']),
    maxResults: z.number().int().min(1).max(50),
    matchThreshold: z.number().min(0).max(1),
});
export const researchEvidenceCaseSchema = z.strictObject({
    revision: researchCaseRevisionSchema,
    mappings: researchMappingsSchema,
    passages: z
        .array(z.strictObject({
        passage: researchPassageSchema,
        source: researchSourceVersionSchema,
    }))
        .min(1)
        .max(30),
    relevance: z.number(),
});
const selection = z.strictObject({
    selectionId: id,
    request: researchEvidenceRequestSchema,
    requestHash: hash,
    materialHash: hash,
    settings: researchRetrievalSettingsSchema,
    provider: text,
    retrievedAt: time,
    catalogueRevision: z.number().int().nonnegative(),
    eligibleCount: z.number().int().nonnegative(),
    readyCount: z.number().int().nonnegative(),
    candidateCount: z.number().int().nonnegative(),
    omissions: z.array(z.strictObject({ key: text, reason: text })),
});
export const researchEvidenceSelectionSchema = z.discriminatedUnion('status', [
    selection.extend({ status: z.literal('selected'), cases: z.array(researchEvidenceCaseSchema).min(1).max(10) }),
    selection.extend({ status: z.literal('no_match'), reason: z.enum(['empty_catalogue', 'no_returned_match']) }),
    selection.extend({ status: z.literal('unsupported_scope'), reason: text }),
    selection.extend({ status: z.literal('unavailable'), reason: text }),
]);
const evidenceUse = {
    alternativeId: id.nullable(),
    passageRefs: z.array(hash).min(1).max(30),
    relation: z.enum(['supports', 'qualifies', 'challenges']),
    rationale: text.max(4000),
    applicabilityLimit: text.max(4000),
};
/** What an AI provider may cite. OpenAI strict output requires every property, so no optional `cardId`; cards
 * are cited by code (research/cards), never by a provider. */
export const researchEvidenceProviderUseSchema = z
    .strictObject({ target: z.enum(['field_choice', 'alternative']), ...evidenceUse })
    .refine((use) => (use.target === 'alternative') === (use.alternativeId !== null), 'Evidence target mismatch');
export const researchEvidenceUseSchema = z
    .strictObject({ target: z.enum(['field_choice', 'alternative', 'card']), ...evidenceUse, cardId: id.optional() })
    .refine((use) => (use.target === 'alternative') === (use.alternativeId !== null), 'Evidence target mismatch')
    .refine((use) => (use.target === 'card') === (use.cardId !== undefined), 'Card target mismatch');
export const researchProvenanceSchema = z.discriminatedUnion('status', [
    z.strictObject({ status: z.literal('not_requested') }),
    z.strictObject({
        status: z.literal('retained'),
        selection: researchEvidenceSelectionSchema,
        evidenceUses: z.array(researchEvidenceUseSchema).max(40),
        newerCaseIds: z.array(id).max(10).optional(),
    }),
    z.strictObject({ status: z.literal('restricted'), selectionId: id, reason: text }),
]);
/** A model can select references; it cannot manufacture source authority. */
export function validateResearchEvidenceUses(uses, selected, alternativeIds) {
    const allowed = new Set(selected.status === 'selected' ? selected.cases.flatMap((c) => c.passages.map((p) => p.passage.passageId)) : []);
    for (const input of uses) {
        const use = researchEvidenceUseSchema.parse(input);
        if (use.target === 'card' ||
            use.passageRefs.some((ref) => !allowed.has(ref)) ||
            (use.alternativeId !== null && !alternativeIds.includes(use.alternativeId)))
            throw new Error('RESEARCH_EVIDENCE_USE_INVALID');
    }
}
//# sourceMappingURL=research-library.js.map